import { useEffect, useState } from "react";
import { CalendarClock, ChevronLeft, ChevronRight, MessageCircle, PhoneOff } from "lucide-react";
import { api, apiErrorDetail } from "@/lib/api";
import { formatDateIt, patientWhatsAppUrl, buildAdminReminderMessage } from "@/lib/site";

const DAY_MS = 24 * 60 * 60 * 1000;
const addDays = (ds, n) => new Date(new Date(ds + "T12:00:00").getTime() + n * DAY_MS).toLocaleDateString("en-CA");

/* Lista dei clienti a cui inviare il promemoria il giorno prima: orario, digiuno e analisi
   nel messaggio WhatsApp precompilato. L'invio è sempre un tocco di Francesca, mai automatico. */
export default function PromemoriaTab({ flash, contact }) {
  const [date, setDate] = useState(addDays(new Date().toLocaleDateString("en-CA"), 1));
  const [items, setItems] = useState(null);
  const [servicesList, setServicesList] = useState([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/admin/appointments", { params: { from_date: date, to_date: date } }),
      api.get("/site").then(({ data }) => (data.services || []).filter((s) => s.active !== false)),
    ])
      .then(([{ data }, svcs]) => {
        if (cancelled) return;
        setItems((data.appointments || []).filter((a) => a.status === "confirmed"));
        setServicesList(svcs);
      })
      .catch((e) => {
        if (!cancelled) flash(apiErrorDetail(e), false);
      });
    return () => {
      cancelled = true;
    };
  }, [date]);

  const modeOf = (a) => servicesList.find((s) => s.id === a.service_id)?.mode || "studio";

  const sendReminder = (a) => {
    const msg = buildAdminReminderMessage({ name: a.name, service: a.service_name, date: a.date, time: a.start, end: a.end, mode: modeOf(a), serviceId: a.service_id });
    window.open(patientWhatsAppUrl(a.phone, msg), "_blank", "noopener,noreferrer");
  };

  const fmt = (ds) => {
    const d = new Date(ds + "T12:00:00");
    const today = new Date().toLocaleDateString("en-CA");
    const label = new Intl.DateTimeFormat("it-IT", { weekday: "long", day: "numeric", month: "long" }).format(d);
    if (ds === addDays(today, 1)) return label + " (domani)";
    if (ds === today) return label + " (oggi)";
    return label;
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display text-2xl lg:text-3xl flex items-center gap-3">
          <CalendarClock size={24} className="text-deeptaupe" /> Promemoria del giorno prima
        </h2>
        <p className="text-sm text-charcoal/60 mt-2 max-w-2xl">
          I clienti con appuntamento confermato, pronti per il promemoria: il pulsante apre la chat WhatsApp col
          messaggio adatto al servizio — per la prima visita ricorda i documenti da inviare e le indicazioni per la
          bioimpedenziometria (digiuno, idratazione, attività fisica…), per il controllo un promemoria breve con l&apos;orario.
          L&apos;invio è sempre tuo.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="promemoria-nav">
        <div className="flex items-center gap-2">
          <button onClick={() => setDate(addDays(date, -1))} data-testid="promemoria-prev" className="px-4 py-2 rounded-full border border-taupe/30 hover:bg-peach" aria-label="Giorno precedente">←</button>
          <button onClick={() => setDate(addDays(new Date().toLocaleDateString("en-CA"), 1))} data-testid="promemoria-domani" className="rounded-full border border-deeptaupe/40 text-deeptaupe text-[12px] uppercase tracking-widest2 font-medium px-5 py-2 hover:bg-peach transition-colors">
            Domani
          </button>
          <button onClick={() => setDate(addDays(date, 1))} data-testid="promemoria-next" className="px-4 py-2 rounded-full border border-taupe/30 hover:bg-peach" aria-label="Giorno successivo">→</button>
        </div>
        <p className="font-display text-xl capitalize flex-1 min-w-[180px] text-center" data-testid="promemoria-date">
          {fmt(date)}
        </p>
      </div>

      {items === null ? (
        <p className="py-16 text-center text-charcoal/50 text-sm" data-testid="promemoria-loading">Caricamento dei promemoria…</p>
      ) : items.length === 0 ? (
        <p className="py-16 text-center text-charcoal/50 text-sm bg-white rounded-3xl" data-testid="promemoria-empty">
          Nessun appuntamento confermato per questo giorno: nessun promemoria da inviare.
        </p>
      ) : (
        <div className="space-y-2" data-testid="promemoria-list">
          {items.map((a) => {
            const hasPhone = !!(a.phone && a.phone.trim());
            return (
              <div key={a.mid} className="rounded-2xl bg-ivory px-3 py-2.5 sm:px-4 sm:py-3" data-testid={`promemoria-row-${a.mid}`}>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <span className="font-display text-lg sm:text-xl text-deeptaupe w-20 sm:w-28">{a.start}–{a.end}</span>
                  <span className="flex-1 min-w-[140px]">
                    <span className="block text-[13px] sm:text-sm font-medium">{a.name}</span>
                    <span className="block text-[12px] sm:text-[13px] text-charcoal/60">{a.service_name} · {a.phone || "—"}</span>
                  </span>
                  {hasPhone ? (
                    <button
                      onClick={() => sendReminder(a)}
                      data-testid={`promemoria-wa-${a.mid}`}
                      className="inline-flex items-center justify-center gap-2 rounded-full border border-[#1f7a4d]/40 text-[#1f7a4d] text-[12px] uppercase tracking-widest2 font-medium px-5 py-2.5 hover:bg-[#1f7a4d]/10 transition-colors"
                    >
                      <MessageCircle size={14} /> Promemoria su WhatsApp
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-2 rounded-full border border-taupe/30 text-charcoal/50 text-[12px] uppercase tracking-widest2 font-medium px-4 py-2.5" data-testid={`promemoria-nophone-${a.mid}`}>
                      <PhoneOff size={13} /> Numero mancante
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          <p className="text-[12px] text-charcoal/50 mt-4">
            Ogni mattina alle 8:00 ricevi anche via email la stessa lista con i pulsanti WhatsApp, per gli appuntamenti confermati del giorno dopo.
          </p>
        </div>
      )}
    </div>
  );
}
